import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Loader from '../shared/Loader';
import { FontSizes, Heading, Copy } from '@hexure/ui';
import Icon from '@mdi/react';
import { mdiClipboardText } from '@mdi/js';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
`;

const SectionTitle = styled.h3`
  font-size: ${FontSizes.MEDIUM};
  font-weight: 450;
  margin-bottom: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 40px;
`;

const DetailsSpan = styled.span`
  font-size: 14px;
  font-weight: 450;
  font-family: Roboto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  border: 1px solid #e5e5e5;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px;
  font-weight: 400;
  border: 1px solid #ddd;
  background-color: #f9f9f9;
  font-size: 14px;
`;

const TableCell = styled.td`
  padding: 12px;
  border: 1px solid #ddd;
  font-size: ${FontSizes.SMALL};
`;

const Details = styled.div`
  font-size: ${FontSizes.SMALL};
  line-height: 1.6;
`;

const TableScrollContainer = styled.div`
  max-width: 70%;
  max-height: 300px;
  overflow-x: auto;
  overflow-y: auto;
`;

const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  text-align: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border: 2px dashed #dee2e6;
  margin: 20px 0;
`;

const NoDataIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: #6c757d;
`;

const NoDataTitle = styled.h3`
  font-size: ${FontSizes.MEDIUM};
  font-weight: 500;
  color: #495057;
  margin: 0 0 8px 0;
`;

const NoDataMessage = styled.p`
  font-size: ${FontSizes.SMALL};
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
`;

const StepUpsSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e5e5;
`;

const StepUpsHeading = styled.h4`
  font-size: ${FontSizes.SMALL};
  font-weight: 500;
  margin-bottom: 12px;
  color: #333;
`;

const formatRate = value => {
  if (!value && value !== 0) return 'N/A';

  // Helper function to round rates to nearest 0.05%
  const roundToNearestFiveHundredths = rate => {
    if (!rate && rate !== 0) return 0;
    const numericRate = parseFloat(rate);
    if (isNaN(numericRate)) return 0;
    // Convert to decimal if it's a percentage over 1
    const percentageRate = numericRate > 1 ? numericRate / 100 : numericRate;
    // Round to nearest 0.0005 (which is 0.05%)
    const rounded = Math.round(percentageRate / 0.0005) * 0.0005;
    return rounded * 100; // Convert back to percentage
  };

  const roundedValue = roundToNearestFiveHundredths(value);
  return `${roundedValue.toFixed(2)}%`;
};

const RiderInfo = ({ data, loading }) => {
  if (loading) return <Loader />;
  // Check if there's no data or if all riders have null age_band_single_rates
  if (!data || data.length === 0 || data.every(rider => !rider?.age_band_single_rates)) {
    return (
      <Container>
        <NoDataContainer>
          <NoDataIcon>
            <Icon path={mdiClipboardText} size={2} color='#6c757d' />
          </NoDataIcon>
          <NoDataTitle>No Rider Information Available</NoDataTitle>
          <NoDataMessage>There are currently no rider details to display for this product.</NoDataMessage>
        </NoDataContainer>
      </Container>
    );
  }

  return (
    <Container>
      {data?.map((rider, idx) => {
        // Skip riders with null age_band_single_rates
        if (!rider?.age_band_single_rates) {
          return null;
        }

        const ageBands = Object.entries(rider.age_band_single_rates);
        const parsedAgeBands = ageBands.map(([range, rate]) => {
          const values = Array.isArray(rate) ? rate : rate.split(',');
          return {
            range,
            single: values[0],
            joint: values[1]?.trim() ?? 'N/A',
            gender: values[2]?.trim() ?? ''
          };
        });

        const hasGender = rider.gender && rider.gender !== '' && rider.gender !== 'N/A';
        const riderName = rider?.rider_variant ? `${rider?.name} (${rider.rider_variant})` : rider?.name;

        const hasStepUps = (rider?.RollupYr || rider?.RollupPct) &&
            (rider?.RollupYr !== null && rider?.RollupYr !== '') ||
            (rider?.RollupPct !== null && rider?.RollupPct !== '');

        return (
          <React.Fragment key={riderName || idx}>
            <Heading type='tertiary'>{riderName}</Heading>
            <Copy style={{ marginTop: '20px' }}>{rider?.description}</Copy>
            <SectionTitle>Age Bands</SectionTitle>
            <InfoRow>
              <TableScrollContainer>
                <Table>
                  <thead>
                    <tr>
                      <TableHeader>Age</TableHeader>
                      <TableHeader>Single</TableHeader>
                      <TableHeader>Joint</TableHeader>
                      {hasGender && <TableHeader>Gender</TableHeader>}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedAgeBands.map(({ range, single, joint, gender }) => (
                      <tr key={range}>
                        <TableCell>{range}</TableCell>
                        <TableCell>{formatRate(single)}</TableCell>
                        <TableCell>{formatRate(joint)}</TableCell>
                        {hasGender && <TableCell>{rider.gender}</TableCell>}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableScrollContainer>
              <Details>
                <Copy>
                  <DetailsSpan>Waiting Period:</DetailsSpan> {rider?.waiting_period ? `${rider.waiting_period} Contract Year` : 'N/A'}
                </Copy>
                <Copy>
                  <DetailsSpan>Cancellation Term:</DetailsSpan> {rider?.cancellation_terms || 'N/A'}
                </Copy>
                <Copy>
                  <DetailsSpan>Charge Basis:</DetailsSpan> {rider?.charge_basis || 'N/A'}
                </Copy>
                <Copy>
                  <DetailsSpan>Charge:</DetailsSpan> {formatRate(rider?.charge ? `${Math.trunc(rider.charge * 100) / 100}` : 'N/A')}
                </Copy>

                {hasStepUps && (
                  <StepUpsSection>
                    <StepUpsHeading>Step Ups:</StepUpsHeading>
                    <Copy>
                      <DetailsSpan>Maximum Years:</DetailsSpan> {rider?.rollup_yr || 'N/A'}
                    </Copy>
                    <Copy>
                      <DetailsSpan>Percentage:</DetailsSpan> {rider?.rollup_pct ? formatRate(rider.rollup_pct) : 'N/A'}
                    </Copy>
                  </StepUpsSection>
                )}
              </Details>
            </InfoRow>
            {idx < data.length - 1 && <hr style={{ margin: '32px 0' }} />}
          </React.Fragment>
        );
      })}
    </Container>
  );
};

RiderInfo.propTypes = {
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool
};

export default RiderInfo;
